/**
 * ParcoursDecouverteHeroPreview
 * --------------------------------------------------------------------
 * Aperçu produit (codé, pas de photo) pour le hero de la home :
 * une fenêtre d'application montrant un candidat qui traverse le
 * Parcours Découverte étape par étape (Entreprise → Poste → Équipe →
 * Rémunération), puis confirme sa candidature.
 *
 * L'animation tourne en boucle. Avec « réduire les animations » activé
 * côté système, on affiche directement l'état final, sans mouvement.
 */

import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Building2,
  Briefcase,
  Users,
  Wallet,
  Check,
  CheckCircle2,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';

const steps = [
  { icon: Building2, label: "L'entreprise", detail: 'Culture, valeurs, locaux' },
  { icon: Briefcase, label: 'Le poste', detail: 'Missions réelles, journée type' },
  { icon: Users, label: "L'équipe", detail: 'Manager et collègues en vidéo' },
  { icon: Wallet, label: 'La rémunération', detail: 'Fixe, variable, avantages' },
] as const;

// Nombre d'étapes « faites » : 0 → 4, puis 5 = candidature confirmée.
const FINAL = steps.length + 1;
const TICK_MS = 1400;

export default function ParcoursDecouverteHeroPreview() {
  const reduceMotion = useReducedMotion();
  const [done, setDone] = useState(reduceMotion ? FINAL : 0);

  useEffect(() => {
    if (reduceMotion) {
      setDone(FINAL);
      return;
    }
    const id = window.setInterval(() => {
      setDone((d) => (d >= FINAL + 1 ? 0 : d + 1)); // pause d'un tick sur l'état final
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [reduceMotion]);

  const completed = Math.min(done, steps.length);
  const confirmed = done >= FINAL;
  const progress = Math.round((completed / steps.length) * 100);

  return (
    <div className="relative w-full max-w-[500px] mx-auto py-10" aria-hidden>
      {/* Halo de fond */}
      <div className="absolute inset-6 rounded-full bg-gradient-to-br from-[#d03484]/20 via-transparent to-[#d47254]/20 blur-3xl" />

      {/* Fenêtre d'application */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="relative z-10 rounded-2xl bg-white shadow-2xl shadow-[#d03484]/15 ring-1 ring-neutral-200 overflow-hidden"
      >
        {/* Barre de fenêtre */}
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-neutral-100 bg-neutral-50/80">
          <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="ml-3 text-[11px] font-medium text-neutral-400">
            Parcours Découverte
          </span>
        </div>

        <div className="p-5 md:p-6">
          {/* En-tête candidat */}
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center text-white text-sm font-bold">
              CL
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-neutral-900 leading-tight">
                Camille L.
              </p>
              <p className="text-xs text-neutral-500 leading-tight">
                Responsable commercial·e · PME industrielle, Marseille
              </p>
            </div>
          </div>

          {/* Progression */}
          <div className="mt-5">
            <div className="flex justify-between text-[11px] font-medium text-neutral-500 mb-1.5">
              <span>Découverte du poste</span>
              <span className="tabular-nums">{progress} %</span>
            </div>
            <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254]"
                animate={{ width: `${progress}%` }}
                transition={{ duration: reduceMotion ? 0 : 0.5, ease: 'easeOut' }}
              />
            </div>
          </div>

          {/* Étapes */}
          <ul className="mt-5 space-y-2.5">
            {steps.map((step, i) => {
              const isDone = i < completed;
              const isCurrent = i === completed && !confirmed;
              const Icon = step.icon;
              return (
                <li
                  key={step.label}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ring-1 transition-colors duration-300 ${
                    isCurrent
                      ? 'bg-[#fdf2f8] ring-[#d03484]/40'
                      : 'bg-white ring-neutral-100'
                  }`}
                >
                  <div
                    className={`h-9 w-9 flex-shrink-0 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                      isDone
                        ? 'bg-gradient-to-br from-[#d03484] to-[#d47254] text-white'
                        : 'bg-neutral-100 text-neutral-400'
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-neutral-900 leading-tight">
                      {step.label}
                    </p>
                    <p className="text-xs text-neutral-500 leading-tight truncate">
                      {step.detail}
                    </p>
                  </div>
                  <div
                    className={`h-6 w-6 flex-shrink-0 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isDone
                        ? 'bg-emerald-500 text-white scale-100'
                        : 'bg-neutral-100 text-transparent scale-90'
                    }`}
                  >
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Résultat */}
          <div
            className={`mt-5 flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-500 ${
              confirmed
                ? 'bg-emerald-50 text-emerald-700 opacity-100'
                : 'bg-neutral-50 text-neutral-400 opacity-70'
            }`}
          >
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            {confirmed
              ? 'Candidature confirmée en connaissance de cause'
              : 'Le candidat découvre le poste…'}
          </div>
        </div>
      </motion.div>

      {/* Chiffres clés flottants */}
      <StatChip
        className="absolute top-0 -right-2 lg:-right-6"
        icon={<TrendingDown className="w-4 h-4" />}
        value="-73 %"
        label="de no-show"
        delay={0.5}
      />
      <StatChip
        className="absolute bottom-20 -right-2 lg:-right-6"
        icon={<TrendingUp className="w-4 h-4" />}
        value="+40 %"
        label="de rétention à 12 mois"
        delay={0.7}
      />
    </div>
  );
}

function StatChip({
  className,
  icon,
  value,
  label,
  delay,
}: {
  className: string;
  icon: React.ReactNode;
  value: string;
  label: string;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.4, ease: 'easeOut' }}
      className={`${className} z-20 flex items-center gap-2.5 rounded-2xl bg-white/95 backdrop-blur px-3.5 py-2.5 shadow-xl shadow-neutral-900/10 ring-1 ring-neutral-200`}
    >
      <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center text-white">
        {icon}
      </div>
      <div className="leading-tight">
        <p className="text-base font-extrabold text-neutral-900 tabular-nums">{value}</p>
        <p className="text-[11px] text-neutral-500">{label}</p>
      </div>
    </motion.div>
  );
}
